<template>
  <div data-testid="staff-subscriptions-table">
    <data-table
      ref="datatableRef"
      test-id-prefix="staff-subscriptions"
      :config="tableConfig"
      :data="rows"
      display-mode="table"
      :refreshButton="{ show: true }"
      @button-click="handleButtonClick"
      @refresh="emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Subscriptions_AdminSubscriptionRow } from '../../../contracts/Subscriptions_AdminSubscriptionRow'

const { t } = useI18n()

interface Props {
  rows: (Subscriptions_AdminSubscriptionRow & { fim_carencia_display?: string })[]
}

defineProps<Props>()

const emit = defineEmits<{
  'open-detail': [row: Subscriptions_AdminSubscriptionRow]
  extend: [row: Subscriptions_AdminSubscriptionRow]
  refresh: []
}>()

interface DataTableExposed {
  loadData: () => void
}

const datatableRef = ref<DataTableExposed | null>(null)

const statusLabel = (status: string | undefined): string => {
  const labels: Record<string, string> = {
    active:     t('Ativa'),
    grace:      t('Carência'),
    cancelled:  t('Cancelada'),
    superseded: t('Substituída'),
    pending:    t('Pendente'),
  }
  if (!status) return t('Sem assinatura')
  return labels[status] ?? status
}

const statusSeverity = (status: string | undefined): string => {
  const map: Record<string, string> = {
    active:     'success',
    grace:      'warn',
    cancelled:  'secondary',
    superseded: 'secondary',
    pending:    'info',
  }
  if (!status) return 'secondary'
  return map[status] ?? 'secondary'
}

const tableConfig = computed(() => ({
  tableId: 'tabelaStaffSubscriptions',
  title: t('Assinaturas'),
  columns: [
    {
      // M3d: the id is the door to the subscription's own house — the detail
      // modal, where Estender and Cancelar live. custom_clickable_id (not
      // clickable_id) because the callback needs the WHOLE row: the modal shows
      // every field, and re-fetching a row the roster already holds would be a
      // second read of the same truth.
      data: 'subscription_id',
      title: t('ID'),
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
        if (row) emit('open-detail', row as unknown as Subscriptions_AdminSubscriptionRow)
      },
    },
    {
      data: 'franchise_name',
      title: t('Franquia'),
      sortable: true,
    },
    {
      data: 'plan_name',
      title: t('Plano'),
      sortable: true,
    },
    {
      // The gateway this subscription is BILLED on (franchises_subscriptions.
      // provider) — the one the cancel path talks to. Blank when the row has no
      // subscription behind it; that emptiness is the truth, not a missing value
      // to fill in.
      data: 'provider',
      title: t('Gateway'),
      sortable: true,
    },
    {
      // The market it was sold in, read from the price row the subscription is
      // pinned to. Blank without a pin — the plan's other price rows are not
      // this subscription's price.
      data: 'currency',
      title: t('Mercado'),
      sortable: true,
    },
    {
      data: 'status',
      title: t('Status'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: (row: Record<string, unknown>) => {
        const status = row.status as string | undefined
        return {
          label: statusLabel(status),
          severity: statusSeverity(status),
          tooltip: statusLabel(status),
        }
      },
    },
    {
      data: 'setup_status',
      title: t('Implantação'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: (row: Record<string, unknown>) => {
        const setupStatus = (row.setup_status as string | undefined) || 'owed'
        const labels: Record<string, string> = {
          owed:   t('Pendente'),
          paid:   t('Paga'),
          exempt: t('Isenta'),
        }
        const severities: Record<string, string> = {
          owed:   'warn',
          paid:   'success',
          exempt: 'info',
        }
        return {
          label: labels[setupStatus] ?? setupStatus,
          severity: severities[setupStatus] ?? 'secondary',
          tooltip: labels[setupStatus] ?? setupStatus,
        }
      },
    },
    {
      data: 'started_at',

      title: t('Início'),
      sortable: true,
      renderType: 'date',
    },
    {
      data: 'fim_carencia_display',
      title: t('Fim/Carência'),
      sortable: false,
    },
    {
      data: 'days_remaining',
      title: t('Dias restantes'),
      sortable: true,
    },
    {
      // M3d moved Estender INTO the detail modal — one house per action. This
      // column survives for the ONE row shape that has no such house: the
      // synthetic franchise-centric row (subscription_id null), emitted so staff
      // can grant a grace window to a franchise with no subscription at all.
      //
      // Deleting it outright would have silently removed the only entry point to
      // that grant — and after a cancellation (terminal, no reactivation by D2)
      // granting grace is precisely the lever the staff has left, so the row a
      // cut produces is exactly the row that needs it.
      data: 'subscription_id',
      title: t('Estender'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Estender'),
      buttonIcon: 'pi pi-calendar-plus',
      buttonAction: 'extend',
      buttonTestId: (row: Record<string, unknown>) =>
        // These rows have subscription_id null — key by franchise (f<franquia_id>).
        `staff-subscriptions-extend-row-f${row.franquia_id as number | string}`,
      buttonCondition: (row: Record<string, unknown>) =>
        row.is_live === true && (row.subscription_id === null || row.subscription_id === undefined),
    },
  ],
  footer: false,
  rowTestId: (row: Record<string, unknown>) => {
    const id = row?.subscription_id ?? (row?.franquia_id != null ? `f${row.franquia_id}` : null)
    return id == null ? '' : `staff-subscriptions-row-${id as number | string}`
  },
}))

function handleButtonClick(e: { value: unknown; data: Subscriptions_AdminSubscriptionRow; action: string }): void {
  if (e.action === 'extend') {
    emit('extend', e.data)
  }
}
</script>
